import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getToday = () => getBSTDate();

/**
 * Sanitize search input before embedding in PostgREST `.or()` / `.ilike()` filter strings.
 * Strips characters that could break the filter syntax or cause unexpected wildcard matching.
 */
export function sanitizeSearchInput(input: string): string {
  return input.replace(/[%_.,()\\]/g, '').trim();
}

/**
 * Get today's date in BST (UTC+6) as YYYY-MM-DD.
 * Use this instead of `new Date().toISOString().split('T')[0]` to avoid
 * midnight-boundary issues (e.g., 11 PM BST → tomorrow in UTC).
 */
export function getBSTDate(date?: Date): string {
  return (date || new Date()).toLocaleDateString('en-CA', { timeZone: 'Asia/Dhaka' });
}

/**
 * Mask a phone number for PII protection in admin/staff views.
 * e.g. "01712345678" → "0171****678", "+8801712345678" → "+880****5678"
 */
export function maskPhone(phone: string | null | undefined): string {
  if (!phone) return '—';
  const cleaned = phone.replace(/\s/g, '');
  // For very short numbers, mask everything except first and last char
  if (cleaned.length <= 6) return cleaned[0] + '*'.repeat(cleaned.length - 2) + cleaned[cleaned.length - 1];
  // For standard numbers: show first 3 + last 3, mask the rest
  const start = cleaned.slice(0, 3);
  const end = cleaned.slice(-3);
  const masked = '*'.repeat(cleaned.length - 6);
  return `${start}${masked}${end}`;
}

/**
 * Normalize a Bangladeshi phone number to canonical 11-digit form: 01XXXXXXXXX
 * Handles: 01616510037, +8801616510037, 8801616510037, 880-1616510037
 * Strips all non-digit characters, removes +88 / 88 country prefix.
 */
export function normalizePhone(phone: string): string {
  // Remove all non-digit characters
  let digits = phone.replace(/\D/g, '');
  // Strip leading 880 country code (common BD prefix)
  if (digits.startsWith('880') && digits.length > 11) {
    digits = '0' + digits.slice(3);
  }
  // If still > 11 digits and starts with 88, strip it
  if (digits.startsWith('88') && digits.length > 11) {
    digits = '0' + digits.slice(2);
  }
  // Ensure it starts with 0
  if (!digits.startsWith('0') && digits.length === 10) {
    digits = '0' + digits;
  }
  return digits;
}

/**
 * Format a monetary value safely for display.
 * - null / undefined → "—" (fare unavailable)
 * - 0 with showZero=false (default) → "—"
 * - 0 with showZero=true → "৳0"
 * - 600 → "৳600"
 *
 * Use showZero=true only when a confirmed payment of ৳0 is meaningful.
 */
export function formatMoney(
  value: number | null | undefined,
  options?: { showZero?: boolean; withSymbol?: boolean }
): string {
  const { showZero = false, withSymbol = true } = options || {};
  if (value === null || value === undefined) return '—';
  if (value === 0 && !showZero) return '—';
  const formatted = value.toLocaleString('en-BD');
  return withSymbol ? `৳${formatted}` : formatted;
}
